import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { useApp } from "../context/AppContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../utils/api";
import { t } from "../utils/i18n";
import LiveRecordModal, { RealComposite } from "./LiveRecordModal";
import VenueVibesViewer, { VenueVibeItem } from "./VenueVibesViewer";
import MediaStoryCard from "./MediaStoryCard";
import { MediaProviderId, PROVIDER_META } from "./MediaPlatformIcons";
import { MOOD_ICONS, MOOD_WISH_ICONS, moodLabelKey, wishLabelKey } from "../utils/moodKeys";
import { useDateState } from "../hooks/useDateState";
import DateOrganizerSheet from "./date/DateOrganizerSheet";

type StoryItem = {
  id: string; media_url: string | null; media_type: "image" | "video"; caption?: string;
  link_type?: "publication" | "ritual" | "community"; link_id?: string; link_title?: string;
  created_at: string; viewed_by_me: boolean; liked_by_me: boolean; likes_count: number;
  kind: "vibe" | "real" | "media"; secondary_media_url?: string | null;
  external_provider?: MediaProviderId | null; external_url?: string | null;
  external_title?: string | null; external_thumbnail_url?: string | null;
  mood_key?: string | null; mood_wish_key?: string | null;
};
type StoryGroup = {
  user_id: string; user_name: string; user_avatar?: string; is_me: boolean;
  has_unseen: boolean; has_real: boolean; stories: StoryItem[];
};
type VenueGroup = { venue_id: string; venue_name: string; venue_avatar?: string; has_real: boolean; item_count: number };

const IMAGE_DURATION_MS = 5000;
const MAX_VIDEO_DURATION_MS = 15000;

const initials = (name?: string) => (name || "?").split(" ").map(p => p[0]).join("").slice(0, 2).toUpperCase();
const timeAgo = (dateStr: string) => {
  const min = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
  if (min < 1) return "à l'instant";
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  return h < 24 ? `${h}h` : `${Math.floor(h / 24)}j`;
};

const PlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);
const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
const TrashIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
  </svg>
);
const HeartIcon = ({ filled }: { filled?: boolean }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill={filled ? "white" : "none"} stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z"/>
  </svg>
);
const EyeIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);
// "A toppé" — badge affiché à côté du nom dans la liste "Vu par", pour distinguer d'un coup
// d'œil qui a juste vu la vibe de qui l'a aussi topée (même symbole ★ que le reste de l'app,
// mais en vraie icône SVG plutôt qu'un simple glyphe texte, comme demandé).
const TopStarIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="#0a0a0a">
    <polygon points="12 2.5 15.1 9 22 10 17 15.1 18.2 22 12 18.6 5.8 22 7 15.1 2 10 8.9 9" />
  </svg>
);

// Dégradé Real — REMPLACE le fond noir/gris habituel de l'anneau (statut "non vue"), jamais en
// plus : un seul anneau à la fois (décision explicite du fondateur, cf. plan Explorer+Vibes).
const REAL_RING_GRADIENT = "linear-gradient(135deg, #FF3B30, #FF7A45)";

// Rangée de "bulles" style Instagram/Facebook en haut du fil : anneau plein pour un ami avec
// une story non encore vue, anneau discret sinon — plus la bulle "Ma story" (créer/consulter).
export default function StoriesBar() {
  const { user, lang } = useApp();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [groups, setGroups] = useState<StoryGroup[]>([]);
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  const [viewerStoryId, setViewerStoryId] = useState<string | undefined>(undefined);
  const [showCreate, setShowCreate] = useState(false);
  const [venueGroups, setVenueGroups] = useState<VenueGroup[]>([]);
  const [venueViewerOpen, setVenueViewerOpen] = useState(false);
  const [venueViewerItems, setVenueViewerItems] = useState<VenueVibeItem[]>([]);
  const [loadingVenueViewer, setLoadingVenueViewer] = useState(false);

  const load = () => {
    api.get("/stories/feed").then(data => { setGroups(data?.groups || []); setVenueGroups(data?.venue_groups || []); }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  // Bulle "lieu" — ce qui s'y passe (vibes/lives/reals tagués), pas mes propres stories : ouvre
  // le même viewer partagé que la fiche du lieu (anneau sur la photo carrée).
  const openVenueViewer = (venueId: string) => {
    setVenueViewerOpen(true);
    setLoadingVenueViewer(true);
    api.get(`/venues/${venueId}/vibes`).then(data => setVenueViewerItems(data?.items || [])).catch(() => setVenueViewerItems([])).finally(() => setLoadingVenueViewer(false));
  };

  // Deep-link depuis une notification "new_real" (?real=<storyId>) — une fois les groupes
  // chargés, on retrouve celui qui contient cette story et on ouvre le viewer dessus directement
  // (pas juste la première story du groupe), puis on nettoie l'URL pour ne pas rouvrir en boucle.
  useEffect(() => {
    const realId = searchParams.get("real");
    if (!realId || groups.length === 0) return;
    const idx = groups.findIndex(g => g.stories.some(s => s.id === realId));
    if (idx >= 0) {
      setViewerIndex(idx);
      setViewerStoryId(realId);
    }
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);

  const myGroup = groups.find(g => g.is_me);
  const others = groups.filter(g => !g.is_me);

  return (
    <>
      <div className="oltodo-no-scrollbar" style={{ display: "flex", gap: 14, overflowX: "auto", padding: "2px 2px 18px", touchAction: "pan-x" }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, cursor: "pointer", flexShrink: 0, width: 64 }}>
          <div style={{ position: "relative" }}>
            <div
              onClick={() => myGroup ? setViewerIndex(groups.indexOf(myGroup)) : setShowCreate(true)}
              style={{ width: 60, height: 60, borderRadius: "50%", padding: 2.5, background: myGroup?.has_real ? REAL_RING_GRADIENT : myGroup ? "#0a0a0a" : "#E0E0E0", display: "flex", alignItems: "center", justifyContent: "center" }}
            >
              <div style={{ width: "100%", height: "100%", borderRadius: "50%", border: "2.5px solid white", overflow: "hidden", backgroundColor: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 15 }}>
                {user?.avatar_url ? <img src={user.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(user?.name)}
              </div>
            </div>
            <button onClick={() => setShowCreate(true)} style={{ position: "absolute", bottom: -1, right: -1, width: 21, height: 21, borderRadius: "50%", backgroundColor: "#0a0a0a", border: "2.5px solid white", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
              <PlusIcon />
            </button>
          </div>
          <span style={{ fontSize: 11, color: "#666", fontWeight: 500, whiteSpace: "nowrap" }}>{t(lang, "stories_my_vibe")}</span>
        </div>

        {others.map(g => (
          <div key={g.user_id} onClick={() => setViewerIndex(groups.indexOf(g))} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, cursor: "pointer", flexShrink: 0, width: 64 }}>
            <div style={{ position: "relative" }}>
              <div style={{ width: 60, height: 60, borderRadius: "50%", padding: 2.5, background: g.has_real ? REAL_RING_GRADIENT : g.has_unseen ? "linear-gradient(135deg, #0a0a0a, #555)" : "#E0E0E0" }}>
                <div style={{ width: "100%", height: "100%", borderRadius: "50%", border: "2.5px solid white", overflow: "hidden", backgroundColor: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 15 }}>
                  {g.user_avatar ? <img src={g.user_avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(g.user_name)}
                </div>
              </div>
            </div>
            <span style={{ fontSize: 11, color: "#666", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 64 }}>{g.user_name.split(" ")[0]}</span>
          </div>
        ))}

        {/* Bulles "lieux" — les lieux que je suis avec du vibe/live/real actif en ce moment.
            Carré (pas rond) pour garder le langage visuel des lieux ; mêmes anneaux bleu/rouge
            que la fiche du lieu et la carte Explorer. */}
        {venueGroups.map(v => {
          const base = (
            <div style={{ width: 52, height: 52, borderRadius: 12, overflow: "hidden", backgroundColor: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 14 }}>
              {v.venue_avatar ? <img src={v.venue_avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(v.venue_name)}
            </div>
          );
          const withRing = (
            <div style={{ padding: 4, border: `3px solid ${v.has_real ? "#FF3B30" : "#3B82F6"}`, borderRadius: 15, display: "inline-flex" }}>{base}</div>
          );
          return (
            <div key={v.venue_id} onClick={() => openVenueViewer(v.venue_id)} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, cursor: "pointer", flexShrink: 0, width: 64 }}>
              {withRing}
              <span style={{ fontSize: 11, color: "#666", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 64 }}>{v.venue_name}</span>
            </div>
          );
        })}
      </div>

      {showCreate && (
        <LiveRecordModal kind="story" onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />
      )}

      {venueViewerOpen && (
        <VenueVibesViewer items={venueViewerItems} loading={loadingVenueViewer} onClose={() => setVenueViewerOpen(false)} />
      )}

      {viewerIndex !== null && groups[viewerIndex] && (
        <StoryViewer
          groups={groups}
          startIndex={viewerIndex}
          startStoryId={viewerStoryId}
          onClose={() => { setViewerIndex(null); setViewerStoryId(undefined); load(); }}
          onNavigate={(type, id) => { setViewerIndex(null); setViewerStoryId(undefined); navigate(`/${type}/${id}`); }}
        />
      )}
    </>
  );
}

const LINK_LABEL_KEYS: Record<string, string> = { publication: "stories_link_event", ritual: "publish_as_type_ritual", community: "publish_as_type_community" };

function StoryViewer({ groups, startIndex, startStoryId, onClose, onNavigate }: {
  groups: StoryGroup[]; startIndex: number; startStoryId?: string; onClose: () => void; onNavigate: (type: string, id: string) => void;
}) {
  const { lang } = useApp();
  const [groupIndex, setGroupIndex] = useState(startIndex);
  const [storyIndex, setStoryIndex] = useState(0);

  // Deep-link : si on nous demande d'ouvrir une story précise (via ?real=) plutôt que la première
  // du groupe, on calcule son index une fois les groupes disponibles.
  useEffect(() => {
    if (!startStoryId) return;
    const g = groups[startIndex];
    if (!g) return;
    const idx = g.stories.findIndex(s => s.id === startStoryId);
    if (idx >= 0) setStoryIndex(idx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startStoryId, startIndex]);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  const [showViewers, setShowViewers] = useState(false);
  const [viewers, setViewers] = useState<any[]>([]);
  const [likeOverrides, setLikeOverrides] = useState<Record<string, { liked: boolean; count: number }>>({});
  const [showInviteOrganizer, setShowInviteOrganizer] = useState(false);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef<number>(0);
  const elapsedRef = useRef<number>(0);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const group = groups[groupIndex];
  const story = group?.stories[storyIndex];
  const isMine = group?.is_me;
  // Toujours instancié (hooks ne peuvent pas être conditionnels) — n'effectue aucune requête
  // tant que sendInvitation n'est pas appelé, donc inoffensif même sans Real Vibe INVITE-MOI.
  const inviteDateState = useDateState(group?.user_id || "");

  const goNext = React.useCallback(() => {
    if (!group) return;
    if (storyIndex < group.stories.length - 1) {
      setStoryIndex(i => i + 1);
    } else if (groupIndex < groups.length - 1) {
      setGroupIndex(i => i + 1);
      setStoryIndex(0);
    } else {
      onClose();
    }
  }, [group, storyIndex, groupIndex, groups.length, onClose]);

  const goPrev = () => {
    if (storyIndex > 0) { setStoryIndex(i => i - 1); return; }
    if (groupIndex > 0) {
      const prevGroup = groups[groupIndex - 1];
      setGroupIndex(i => i - 1);
      setStoryIndex(prevGroup.stories.length - 1);
    }
  };

  // Marque la story courante comme vue (pas la peine pour les siennes)
  useEffect(() => {
    if (story && !isMine) api.post(`/stories/${story.id}/view`, {}).catch(() => {});
  }, [story?.id, isMine]);

  // Progression de la barre — durée fixe pour une image, durée réelle (plafonnée) pour une vidéo
  useEffect(() => {
    setProgress(0);
    elapsedRef.current = 0;
    setPaused(false);
    if (!story) return;
    if (story.media_type === "video") return; // piloté par onTimeUpdate de la vidéo
    const duration = IMAGE_DURATION_MS;
    startRef.current = performance.now();
    const tick = (now: number) => {
      const elapsed = elapsedRef.current + (now - startRef.current);
      const pct = Math.min(100, (elapsed / duration) * 100);
      setProgress(pct);
      if (pct >= 100) { goNext(); return; }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupIndex, storyIndex]);

  const handleVideoTime = () => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    const capped = Math.min(v.duration, MAX_VIDEO_DURATION_MS / 1000);
    setProgress(Math.min(100, (v.currentTime / capped) * 100));
    if (v.currentTime >= capped) goNext();
  };

  const togglePause = () => {
    setPaused(p => {
      const next = !p;
      if (story?.media_type === "video" && videoRef.current) {
        next ? videoRef.current.pause() : videoRef.current.play().catch(() => {});
      } else if (story?.media_type !== "video") {
        if (next) { if (rafRef.current) cancelAnimationFrame(rafRef.current); elapsedRef.current += performance.now() - startRef.current; }
        else { startRef.current = performance.now(); rafRef.current = requestAnimationFrame(function t(now) { const el = elapsedRef.current + (now - startRef.current); const pct = Math.min(100, (el / IMAGE_DURATION_MS) * 100); setProgress(pct); if (pct >= 100) { goNext(); return; } rafRef.current = requestAnimationFrame(t); }); }
      }
      return next;
    });
  };

  const likeState = story ? (likeOverrides[story.id] ?? { liked: story.liked_by_me, count: story.likes_count }) : { liked: false, count: 0 };

  const toggleLike = () => {
    if (!story) return;
    const prev = likeState;
    const next = { liked: !prev.liked, count: prev.count + (prev.liked ? -1 : 1) };
    setLikeOverrides(o => ({ ...o, [story.id]: next }));
    api.post(`/stories/${story.id}/like`, {}).then(data => {
      if (data && typeof data.likes_count === "number") {
        setLikeOverrides(o => ({ ...o, [story.id]: { liked: !!data.liked, count: data.likes_count } }));
      }
    }).catch(() => setLikeOverrides(o => ({ ...o, [story.id]: prev })));
  };

  const openViewers = () => {
    if (!story) return;
    setShowViewers(true);
    api.get(`/stories/${story.id}/viewers`).then(d => setViewers(d?.viewers || [])).catch(() => {});
  };

  const deleteStory = () => {
    if (!story || !window.confirm(t(lang, "stories_delete_confirm"))) return;
    api.delete(`/stories/${story.id}`).then(() => {
      if (group.stories.length <= 1) onClose(); else goNext();
    }).catch(() => {});
  };

  if (!group || !story) return null;

  // Portail direct sur document.body — même bug/fix que DatePromptSheet.tsx : un position:fixed
  // descendant du conteneur mobile de Layout.tsx (WebkitOverflowScrolling:"touch") se positionne
  // sur Safari iOS par rapport au contenu défilant plutôt qu'au vrai viewport.
  return ReactDOM.createPortal(
    <div style={{ position: "fixed", inset: 0, backgroundColor: "black", zIndex: 3000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ position: "relative", width: "100%", maxWidth: 420, height: "100%", maxHeight: 860, backgroundColor: "#111" }}>

        {/* Barres de progression segmentées */}
        <div style={{ position: "absolute", top: 10, left: 10, right: 10, display: "flex", gap: 4, zIndex: 10 }}>
          {group.stories.map((s, i) => (
            <div key={s.id} style={{ flex: 1, height: 2.5, borderRadius: 2, backgroundColor: "rgba(255,255,255,0.35)", overflow: "hidden" }}>
              <div style={{ height: "100%", backgroundColor: "white", width: `${i < storyIndex ? 100 : i === storyIndex ? progress : 0}%`, transition: i === storyIndex ? "none" : "width 0.15s" }} />
            </div>
          ))}
        </div>

        {/* Header — l'avatar/nom mène au profil de l'auteur (pas le sien) pour l'ajouter en
            ami ou le liker, comme le tag d'une personne sur une story Instagram. */}
        <div style={{ position: "absolute", top: 20, left: 12, right: 12, display: "flex", alignItems: "center", gap: 10, zIndex: 10 }}>
          <div
            onClick={isMine ? undefined : () => onNavigate("profile", group.user_id)}
            style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0, cursor: isMine ? "default" : "pointer" }}
          >
            <div style={{ width: 32, height: 32, borderRadius: "50%", overflow: "hidden", backgroundColor: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
              {group.user_avatar ? <img src={group.user_avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(group.user_name)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: "white", fontSize: 13, fontWeight: 700 }}>{isMine ? "Toi" : group.user_name}</div>
              <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 11 }}>{timeAgo(story.created_at)}</div>
            </div>
          </div>
          {isMine && (
            <button onClick={deleteStory} style={{ border: "none", background: "none", cursor: "pointer", padding: 6 }}><TrashIcon /></button>
          )}
          <button onClick={onClose} style={{ border: "none", background: "none", cursor: "pointer", padding: 6 }}><CloseIcon /></button>
        </div>

        {/* Média */}
        <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }} onContextMenu={e => e.preventDefault()}>
          {story.kind === "media" ? (
            <MediaStoryCard provider={story.external_provider!} title={story.external_title || ""} thumbnailUrl={story.external_thumbnail_url} url={null} renderCta={false} />
          ) : story.kind === "real" && story.secondary_media_url ? (
            <RealComposite backUrl={story.media_url!} frontUrl={story.secondary_media_url} />
          ) : story.media_type === "video" ? (
            <video ref={videoRef} src={story.media_url!} autoPlay playsInline muted={false} onTimeUpdate={handleVideoTime} onEnded={goNext} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
          ) : (
            <img src={story.media_url!} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} draggable={false} />
          )}
        </div>

        {/* Zones tap : gauche = précédent, droite = suivant, appui long = pause */}
        <div style={{ position: "absolute", inset: 0, display: "flex", zIndex: 5 }}>
          <div style={{ flex: 1 }} onClick={goPrev} onMouseDown={togglePause} onMouseUp={togglePause} />
          <div style={{ flex: 2 }} onClick={goNext} onMouseDown={togglePause} onMouseUp={togglePause} />
        </div>

        {/* CTA "Regarder/Écouter sur X" d'un Media Vibe — rendu APRÈS les zones de tap
            ci-dessus avec un zIndex supérieur, sinon le clic est intercepté par elles et le
            bouton ne réagit jamais malgré son apparence normale. */}
        {story.kind === "media" && story.external_url && (
          <div style={{ position: "absolute", bottom: isMine ? 130 : 90, left: 14, right: 14, zIndex: 10, display: "flex", justifyContent: "center" }}>
            <a
              href={story.external_url} target="_blank" rel="noopener noreferrer"
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 16px", borderRadius: 20, border: "1px solid rgba(255,255,255,0.4)", backgroundColor: "rgba(255,255,255,0.15)", color: "white", fontSize: 13, fontWeight: 600, textDecoration: "none" }}
            >
              {story.external_provider && `${PROVIDER_META[story.external_provider].verb === "Écouter" ? t(lang, "stories_listen") : t(lang, "stories_watch")} ${t(lang, "stories_on")} ${PROVIDER_META[story.external_provider].label}`} →
            </a>
          </div>
        )}

        {paused && (
          <div style={{ position: "absolute", top: 20, right: 54, color: "white", fontSize: 11, backgroundColor: "rgba(0,0,0,0.5)", padding: "3px 9px", borderRadius: 10, zIndex: 10 }}>Pause</div>
        )}

        {/* Légende + lien vers l'activité concernée (clic = fiche de l'activité, pour la
            rejoindre une prochaine fois) + bouton Top pour saluer la vibe */}
        <div style={{ position: "absolute", bottom: isMine ? 60 : 20, left: 14, right: 14, zIndex: 10, display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {story.kind === "real" && story.mood_key && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, color: story.mood_key === "invite_me" ? "#E8C766" : "white", fontSize: 13, fontWeight: 700, textShadow: "0 1px 4px rgba(0,0,0,0.5)" }}>
                  {(() => { const Icon = MOOD_ICONS[story.mood_key as keyof typeof MOOD_ICONS]; return Icon ? <Icon size={14} strokeWidth={2} /> : null; })()}
                  {t(lang, moodLabelKey(story.mood_key)).toUpperCase()}
                </div>
                {story.mood_key === "invite_me" && story.mood_wish_key && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6, color: "rgba(255,255,255,0.85)", fontSize: 12.5 }}>
                    {(() => { const WIcon = MOOD_WISH_ICONS[story.mood_wish_key as keyof typeof MOOD_WISH_ICONS]; return WIcon ? <WIcon size={13} strokeWidth={2} /> : null; })()}
                    {t(lang, wishLabelKey(story.mood_wish_key))}
                  </div>
                )}
                {story.mood_key === "invite_me" && !isMine && (
                  <button
                    onClick={() => setShowInviteOrganizer(true)}
                    style={{ alignSelf: "flex-start", display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 20, border: "1px solid rgba(232,199,102,0.5)", backgroundColor: "rgba(232,199,102,0.14)", color: "#E8C766", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}
                  >
                    {t(lang, "mood_invite_button")} →
                  </button>
                )}
              </div>
            )}
            {story.caption && <div style={{ color: "white", fontSize: 14, marginBottom: 10, textShadow: "0 1px 4px rgba(0,0,0,0.5)" }}>{story.caption}</div>}
            {story.link_title && story.link_type && story.link_id && (
              <button onClick={() => onNavigate(story.link_type!, story.link_id!)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 14px", borderRadius: 20, border: "1px solid rgba(255,255,255,0.4)", backgroundColor: "rgba(255,255,255,0.15)", color: "white", fontSize: 13, fontWeight: 600, cursor: "pointer", maxWidth: "100%" }}>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t(lang, LINK_LABEL_KEYS[story.link_type] || "stories_link_event")} · {story.link_title}</span> →
              </button>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 16, flexShrink: 0 }}>
            {/* Le/la créateur·rice de la vibe voit seulement "Vu" — "Top" (compter ses propres
                likes) n'a pas de sens sur son propre contenu, jamais les deux à la fois. Même
                taille d'icône (22) que le like pour rester cohérent visuellement. */}
            {isMine ? (
              <button onClick={openViewers} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, border: "none", background: "none", cursor: "pointer", padding: 4 }}>
                <EyeIcon />
                <span style={{ color: "white", fontSize: 11, fontWeight: 700, textShadow: "0 1px 4px rgba(0,0,0,0.5)" }}>{t(lang, "stories_seen")}</span>
              </button>
            ) : (
              <button onClick={toggleLike} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, border: "none", background: "none", cursor: "pointer", padding: 4 }}>
                <div style={{ transform: likeState.liked ? "scale(1.08)" : "scale(1)", transition: "transform 0.15s" }}>
                  <HeartIcon filled={likeState.liked} />
                </div>
                <span style={{ color: "white", fontSize: 11, fontWeight: 700, textShadow: "0 1px 4px rgba(0,0,0,0.5)" }}>{likeState.count > 0 ? likeState.count : t(lang, "top")}</span>
              </button>
            )}
          </div>
        </div>

        {showViewers && (
          <div onClick={() => setShowViewers(false)} style={{ position: "absolute", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 20, display: "flex", alignItems: "flex-end" }}>
            <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: "18px 18px 0 0", padding: 20, width: "100%", maxHeight: "50%", overflowY: "auto" }}>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>{t(lang, "stories_seen_by").replace("{n}", String(viewers.length))}</div>
              {viewers.length === 0 && <div style={{ fontSize: 13, color: "#999" }}>{t(lang, "notif_nobody_yet")}</div>}
              {viewers.map(v => (
                <div key={v.id} onClick={() => onNavigate("profile", v.id)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", cursor: "pointer" }}>
                  <div style={{ width: 32, height: 32, borderRadius: "50%", overflow: "hidden", backgroundColor: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                    {v.avatar_url ? <img src={v.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(v.name)}
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 600, flex: 1, minWidth: 0 }}>{v.name}</span>
                  {v.liked && <TopStarIcon />}
                </div>
              ))}
            </div>
          </div>
        )}

        {showInviteOrganizer && (
          <DateOrganizerSheet
            lang={lang}
            onSend={inviteDateState.sendInvitation}
            onClose={() => setShowInviteOrganizer(false)}
          />
        )}
      </div>
    </div>
  ,
    document.body
  );
}
